import React, { useState } from 'react'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import {
  Box,
  Button,
  Card,
  Heading,
  Text,
  VStack,
  HStack,
  Icon
} from '@chakra-ui/react'
import { FiLock, FiArrowRight } from 'react-icons/fi'

// Get Stripe publishable key from environment
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''

// Initialize Stripe
// Note: In development (HTTP), Stripe will show a warning about HTTPS requirement.
// This is expected and safe to ignore - Stripe.js works over HTTP for testing.
let stripePromise: Promise<Stripe | null> | null = null

const getStripe = () => {
  if (!stripePromise) {
    stripePromise = STRIPE_PUBLISHABLE_KEY
      ? loadStripe(STRIPE_PUBLISHABLE_KEY)
      : Promise.resolve(null)
  }
  return stripePromise
}

interface StripeCheckoutFormProps {
  clientSecret: string
  amount: number
  packName: string
  onSuccess: (paymentIntentId: string) => void
  onError: (error: string) => void
}

const StripeCheckoutFormInner: React.FC<StripeCheckoutFormProps> = ({
  amount,
  onSuccess,
  onError
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      onError('Stripe not loaded. Please refresh the page.')
      return
    }

    setIsProcessing(true)

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      })

      if (error) {
        onError(error.message || 'Payment failed')
        setIsProcessing(false)
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment processing failed'
      onError(errorMessage)
      setIsProcessing(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <VStack gap={4} align="stretch">
        <Box
          p={4}
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="lg"
          bg="white"
        >
          <PaymentElement />
        </Box>
        <HStack gap={2} justify="center" color="gray.600" fontSize="sm">
          <Icon as={FiLock} />
          <Text>Secure payment powered by Stripe</Text>
        </HStack>
        <Button
          type="submit"
          size="lg"
          colorPalette="brand"
          w="full"
          isLoading={isProcessing}
          loadingText="Processing payment..."
          isDisabled={!stripe || isProcessing}
          rightIcon={<Icon as={FiArrowRight} />}
          fontWeight="bold"
        >
          Pay ${(amount / 100).toFixed(0)}
        </Button>
      </VStack>
    </form>
  )
}

export const StripeCheckoutForm: React.FC<StripeCheckoutFormProps> = (props) => {
  const stripePromise = getStripe()

  if (!STRIPE_PUBLISHABLE_KEY) {
    return (
      <Card.Root borderColor="red.200" bg="red.50">
        <Card.Body p={6}>
          <VStack gap={2}>
            <Heading size="sm" color="red.700">
              Stripe not configured
            </Heading>
            <Text fontSize="sm" color="red.600">
              Please set VITE_STRIPE_PUBLISHABLE_KEY in your environment variables.
            </Text>
          </VStack>
        </Card.Body>
      </Card.Root>
    )
  }

  const elementsOptions = {
    clientSecret: props.clientSecret,
    appearance: {
      theme: 'stripe' as const,
      variables: {
        colorPrimary: '#4F46E5',
      },
    },
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <StripeCheckoutFormInner {...props} />
    </Elements>
  )
}

// Export inner component for testing if needed
export { StripeCheckoutFormInner }

